/**
 * "Type a letter anywhere, and the symbol search opens with it in the box."
 *
 * The listener half. **Which** keystrokes qualify is `type-to-search.ts`, a pure
 * predicate, because that is where every edge case lives (modifiers, editable
 * focus, IME composition, non-letters) and none of them need a rendered
 * component to state.
 *
 * ## One listener, owned by the app
 *
 * This used to live inside `<SymbolSearch>`, which is mounted once per chart
 * toolbar. That meant N subscribers to one keystroke, a module-scope set to
 * hold them, a prop to nominate which one answered, and a rule that the oldest
 * won — an elaborate tiebreak for a question that should never have been asked.
 * A search dialog is not per-chart: nothing it shows depends on which chart it
 * belongs to, only where the answer goes. So the app owns the listener, the app
 * knows which chart is selected, and there is exactly one of each.
 *
 * That collapse is not cosmetic. The bug it removes is real: the letter went to
 * whichever box belonged to the *active chart*, clicking the screener changed
 * which chart that was, and the keystroke landed on an instance whose own state
 * was cold — consumed, then silently dropped, for the rest of the session.
 *
 * ## The key is only taken if it is acted on
 *
 * `preventDefault` happens here and only when `onLetter` accepts, because
 * consuming a keystroke and then doing nothing with it is indistinguishable
 * from a dead keyboard. It is taken from the browser as well as from the page:
 * Firefox's "search for text when you start typing" answers the same keystroke,
 * and two search boxes opening at once is nobody's shortcut.
 */

import { useEffect, useRef } from "react";

import { typeToSearchSeed } from "./type-to-search";

/**
 * Call `onLetter` for a letter typed with focus nowhere that wants it.
 *
 * `onLetter` answers whether it took the letter. Returning `false` puts the
 * keystroke back — an app with no symbol service behind it must not swallow
 * every letter typed at it.
 */
export function useTypeToSearch(onLetter: (letter: string) => boolean): void {
  // Through a ref so the subscription is stable: `onLetter` is an inline
  // closure at the call site and re-binding a document listener on every render
  // of the app shell is a lot of churn for a handler that never changes shape.
  const latest = useRef(onLetter);
  latest.current = onLetter;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Something nearer the keystroke already acted on it — the toolbar box's
      // own handler, most often, which prevents the default to carry the
      // character into the dialog. Two handlers for one keypress is how it gets
      // seeded twice.
      if (event.defaultPrevented) return;
      const letter = typeToSearchSeed(event);
      if (letter === null) return;
      if (latest.current(letter)) event.preventDefault();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);
}
